import { Card, Tab, Tabs } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

export type InstrumentHistoryTab<T extends string = string> = {
  id: T;
  label: string;
  content: React.ReactNode;
  visible?: boolean;
};

type InstrumentHistoryTabsProps<T extends string> = {
  tabs: InstrumentHistoryTab<T>[];
  initialTabId?: T;
  onSelect?: (tabId: T) => void;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
  },
  tabs: {
    position: 'relative',
    minHeight: 43,
    '&::after': {
      position: 'absolute',
      right: 0,
      bottom: 0,
      left: 0,
      height: 1,
      backgroundColor: theme.palette.divider,
      content: '""',
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  tab: {
    position: 'relative',
    minHeight: 43,
    width: 160,
    padding: theme.spacing(1.25, 0),
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    color: theme.palette.primary.main,
    textAlign: 'center',
    '&.Mui-selected': {
      zIndex: 1,
      marginBottom: -2,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderTop: `3px solid ${theme.palette.grey[300]}`,
      borderBottomColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      '&::after': {
        position: 'absolute',
        right: 0,
        bottom: -2,
        left: 0,
        zIndex: 2,
        height: 2,
        backgroundColor: theme.palette.background.paper,
        content: '""',
      },
    },
  },
  panel: {
    height: 187,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    borderTop: '0 !important',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
}));

const isVisible = (tab: InstrumentHistoryTab) => tab.visible !== false;

/**
 * 機器詳細の履歴を表示する共通の枠。
 *
 * 各履歴機能は一覧を `content` として渡すだけで追加でき、タブの選択状態や
 * 非表示タブからのフォールバックはこのコンポーネントが管理する。
 */
export default function InstrumentHistoryTabs<T extends string>(props: InstrumentHistoryTabsProps<T>) {
  const { classes } = useStyles();
  const visibleTabs = useMemo(() => props.tabs.filter(isVisible), [props.tabs]);
  const initialTabId = visibleTabs.some((tab) => tab.id === props.initialTabId)
    ? props.initialTabId
    : visibleTabs[0]?.id;
  const [selectedTabId, setSelectedTabId] = useState<T | undefined>(initialTabId);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === selectedTabId)) {
      setSelectedTabId(initialTabId);
    }
  }, [initialTabId, selectedTabId, visibleTabs]);

  useEffect(() => {
    if (selectedTabId) props.onSelect?.(selectedTabId);
  }, [props.onSelect, selectedTabId]);

  if (!selectedTabId) return null;

  return (
    <div className={classes.root}>
      <Tabs
        value={selectedTabId}
        onChange={(_event, value: T) => setSelectedTabId(value)}
        className={classes.tabs}
        variant="scrollable"
        scrollButtons={false}
        aria-label="機器履歴"
      >
        {visibleTabs.map((tab) => (
          <Tab
            key={tab.id}
            id={`instrument-history-tab-${tab.id}`}
            aria-controls={`instrument-history-panel-${tab.id}`}
            className={classes.tab}
            disableRipple
            label={tab.label}
            value={tab.id}
          />
        ))}
      </Tabs>
      <Card className={classes.panel} variant="outlined">
        {visibleTabs.map((tab) => (
          <div
            key={tab.id}
            id={`instrument-history-panel-${tab.id}`}
            role="tabpanel"
            aria-labelledby={`instrument-history-tab-${tab.id}`}
            hidden={tab.id !== selectedTabId}
          >
            {tab.id === selectedTabId && tab.content}
          </div>
        ))}
      </Card>
    </div>
  );
}
